import { FC } from 'react';
import Select, { MultiValue, CSSObjectWithLabel, StylesConfig } from 'react-select';

type MultiSelectProps = {
  options: { value: string; label: string }[];
  onChange: (e: MultiValue<{ value: string; label: string }>) => void;
  disabled?: boolean;
  defaultValues?: { label: string; value: string }[];
  styleOverrides?: StylesConfig<{ value: string; label: string }>;
};

export enum MultiSelectStyles {
  CONTROL = 'control',
  MENU = 'menu',
  MENU_LIST = 'menuList',
  MULTI_VALUE = 'multiValue',
  MULTI_VALUE_LABEL = 'multiValueLabel',
  MULTI_VALUE_REMOVE = 'multiValueRemove',
  INDICATOR_SEPARATOR = 'indicatorSeparator',
  CLEAR_INDICATOR = 'clearIndicator',
  VALUE_CONTAINER = 'valueContainer',
  PLACEHOLDER = 'placeholder',
  DROPDOWN_INDICATOR = 'dropdownIndicator'
}

export const multiSelectStyles: Record<MultiSelectStyles, CSSObjectWithLabel> = {
  [MultiSelectStyles.CONTROL]: {
    boxShadow: 'none',
    border: 'none',
    borderBottom: '1px solid var(--color-contrast-black)',
    borderRadius: '0px'
  },
  [MultiSelectStyles.MENU]: {
    border: '1px solid var(--color-contrast-black)',
    boxShadow: 'none',
    borderRadius: '0px',
    top: '29px'
  },
  [MultiSelectStyles.MENU_LIST]: {
    '::-webkit-scrollbar': {
      width: '4px',
      height: '0px'
    },
    '::-webkit-scrollbar-track': {
      background: '#f1f1f1'
    },
    '::-webkit-scrollbar-thumb': {
      background: '#888'
    },
    '::-webkit-scrollbar-thumb:hover': {
      background: '#555'
    }
  },
  [MultiSelectStyles.MULTI_VALUE]: {
    color: 'var(--color-contrast-black)',
    backgroundColor: 'var(--color-contrast-white)'
  },
  [MultiSelectStyles.MULTI_VALUE_LABEL]: {
    color: 'var(--color-contrast-black)',
    backgroundColor: 'var(--color-contrast-white)',
    borderRadius: '0px',
    fontSize: '1rem'
  },
  [MultiSelectStyles.MULTI_VALUE_REMOVE]: {
    color: 'var(--color-feedback-danger-300)',
    backgroundColor: 'var(--color-contrast-white)',
    border: '1px solid var(--color-feedback-danger-300)',
    padding: '4px',
    cursor: 'pointer',
    borderRadius: '50%',
    ':hover': {
      backgroundColor: 'var(--color-contrast-white)',
      color: 'var(--color-feedback-danger-300)'
    }
  },
  [MultiSelectStyles.INDICATOR_SEPARATOR]: {
    color: 'var(--color-contrast-black)',
    backgroundColor: 'var(--color-contrast-white)',
    borderColor: 'var(--color-contrast-black)'
  },
  [MultiSelectStyles.CLEAR_INDICATOR]: {
    color: 'var(--color-contrast-black)',
    backgroundColor: 'var(--color-contrast-white)',
    borderColor: 'var(--color-contrast-black)'
  },
  [MultiSelectStyles.VALUE_CONTAINER]: {},
  [MultiSelectStyles.PLACEHOLDER]: {
    color: '#c7c7c7',
    backgroundColor: 'transparent'
  },
  [MultiSelectStyles.DROPDOWN_INDICATOR]: {
    color: 'var(--color-contrast-black)'
  }
};

const buildStyles = (disabled?: boolean): StylesConfig<{ value: string; label: string }> => ({
  control: (base: CSSObjectWithLabel) => ({
    ...base,
    ...multiSelectStyles[MultiSelectStyles.CONTROL]
  }),
  menu: (base: CSSObjectWithLabel) => ({
    ...base,
    ...multiSelectStyles[MultiSelectStyles.MENU]
  }),
  dropdownIndicator: (base: CSSObjectWithLabel) => ({
    ...base,
    ...multiSelectStyles[MultiSelectStyles.DROPDOWN_INDICATOR]
  }),
  valueContainer: (base: CSSObjectWithLabel) => ({
    ...base,
    ...multiSelectStyles[MultiSelectStyles.VALUE_CONTAINER],
    ...(disabled
      ? {
          display: 'flex',
          flexDirection: 'column',
          alignContent: 'flex-start',
          'align-items': 'unset',
          '--webkit-align-items': 'left'
        }
      : {})
  }),
  placeholder: (base: CSSObjectWithLabel) => ({
    ...base,
    ...multiSelectStyles[MultiSelectStyles.PLACEHOLDER]
  }),
  menuList: (base: CSSObjectWithLabel) => ({
    ...base,
    ...multiSelectStyles[MultiSelectStyles.MENU_LIST]
  }),
  multiValueLabel: (base: CSSObjectWithLabel) => ({
    ...base,
    ...multiSelectStyles[MultiSelectStyles.MULTI_VALUE_LABEL]
  }),
  indicatorSeparator: (base: CSSObjectWithLabel) => ({
    ...base,
    ...multiSelectStyles[MultiSelectStyles.INDICATOR_SEPARATOR]
  }),
  multiValue: (base: CSSObjectWithLabel) => ({
    ...base,
    ...multiSelectStyles[MultiSelectStyles.MULTI_VALUE]
  }),
  multiValueRemove: (base: CSSObjectWithLabel) => ({
    ...base,
    ...multiSelectStyles[MultiSelectStyles.MULTI_VALUE_REMOVE],
    display: disabled ? 'none' : 'flex'
  }),
  clearIndicator: (base: CSSObjectWithLabel) => ({
    ...base,
    ...multiSelectStyles[MultiSelectStyles.CLEAR_INDICATOR]
  })
});

export const MultiSelect: FC<MultiSelectProps> = (props) => {
  const { options, onChange, disabled, defaultValues = [], styleOverrides } = props;
  const newStyles = styleOverrides ? { ...buildStyles(disabled), ...styleOverrides } : { ...buildStyles(disabled) };

  return (
    <Select
      className=""
      defaultValue={defaultValues}
      isMulti
      closeMenuOnSelect
      options={options}
      onChange={onChange}
      maxMenuHeight={240}
      menuShouldScrollIntoView={true}
      styles={newStyles}
      isDisabled={disabled}
    />
  );
};
